import { useEffect, useRef, useState } from 'react';

import { Button } from './Button.js';
import { Sheet } from './Sheet.js';
import { Spinner } from './Spinner.js';
import { useI18n } from '../i18n/index.js';
import { drawShareCard, type ShareCardInput, type ShareRatio } from '../lib/shareCard.js';
import styles from './ShareSheet.module.css';

interface ShareSheetProps {
  open: boolean;
  onClose: () => void;
  fileName: string;
  /** 비율만 빼고 다 정해져서 온다 — 비율은 여기서 고른다 */
  card: Omit<ShareCardInput, 'ratio'> | null;
  onError: (cause: unknown) => void;
}

const RATIOS: ShareRatio[] = ['square', 'story'];

/**
 * 이미지로 공유하기.
 *
 * **고르고 나서 보여주는 게 아니라 보면서 고른다.** 비율 하나로 담기는 기록
 * 수가 달라지는데(1:1은 세로가 절반이다), 눌러서 받아 보고 나서야 아는
 * 구조면 두 번 받아 보게 된다. 바꿀 때마다 다시 그려 그 자리에서 보여준다.
 */
export function ShareSheet({ open, onClose, fileName, card, onError }: ShareSheetProps) {
  const { t } = useI18n();
  const [ratio, setRatio] = useState<ShareRatio>('story');
  const [url, setUrl] = useState<string | null>(null);
  const [blob, setBlob] = useState<Blob | null>(null);
  const urlRef = useRef<string | null>(null);

  useEffect(() => {
    if (!open || card === null) {
      return;
    }
    /*
     * 그리는 동안 비율이 또 바뀔 수 있다(사진을 불러오느라 몇백 ms 걸린다).
     * 늦게 끝난 옛 그림이 새 그림을 덮지 않게 **버려진 요청은 버린다.**
     */
    let alive = true;
    setUrl(null);
    setBlob(null);
    void drawShareCard({ ...card, ratio })
      .then((made) => {
        if (!alive) {
          return;
        }
        if (urlRef.current !== null) {
          URL.revokeObjectURL(urlRef.current);
        }
        urlRef.current = URL.createObjectURL(made);
        setBlob(made);
        setUrl(urlRef.current);
      })
      .catch((cause: unknown) => {
        if (alive) {
          onError(cause);
        }
      });
    return () => {
      alive = false;
    };
  }, [open, card, ratio, onError]);

  /* 시트가 닫히면 주소를 돌려준다 — 안 그러면 비율을 만질 때마다 하나씩 샌다 */
  useEffect(
    () => () => {
      if (urlRef.current !== null) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
    },
    [],
  );

  const send = async () => {
    if (blob === null) {
      return;
    }
    const file = new File([blob], fileName, { type: 'image/png' });
    /*
     * 폰에서는 사진첩을 거치지 않고 바로 다른 앱에 넘긴다 — 받기·앨범·앱
     * 열기·고르기 네 단계가 한 번 누르기가 된다. 안 되는 곳에서는 내려받는다.
     */
    if (navigator.canShare?.({ files: [file] }) === true) {
      try {
        await navigator.share({ files: [file] });
        return;
      } catch {
        /* 사람이 공유 창을 닫은 것도 여기로 온다 — 조용히 둔다 */
        return;
      }
    }
    if (url === null) {
      return;
    }
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
  };

  return (
    <Sheet open={open} onClose={onClose} title={t('share.sheetTitle')}>
      <div className={styles.ratios}>
        {RATIOS.map((option) => (
          <button
            key={option}
            type="button"
            className={`${styles.ratio} ${styles[option]}`}
            aria-pressed={ratio === option}
            onClick={() => setRatio(option)}
          >
            <span className={styles.ratioShape} aria-hidden="true" />
            <span className={styles.ratioLabel}>{t(`share.${option}`)}</span>
          </button>
        ))}
      </div>

      <div className={styles.preview}>
        {url === null ? (
          <Spinner />
        ) : (
          <img className={styles.previewImage} src={url} alt={t('share.sheetTitle')} />
        )}
      </div>

      <div className={styles.actions}>
        <Button variant="outline" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button disabled={blob === null} onClick={() => void send()}>
          {t('share.send')}
        </Button>
      </div>
    </Sheet>
  );
}
